// AIAgentCardV3 — MOCKED preview card for a single AI Agent.
//
// Status pill colors:
//   active   → green
//   standby  → amber
//   inactive → grey
import React from "react";
import { motion } from "framer-motion";
import { Bot } from "lucide-react";

const STATUS_COLORS = {
    active: { bg: "rgba(255, 255, 255,0.14)", fg: "#FFFFFF" },
    standby: { bg: "rgba(163,163,163,0.14)", fg: "#A3A3A3" },
    inactive: { bg: "rgba(204, 204, 204,0.14)", fg: "#CCCCCC" },
};

// V12 guard rail · reject any saturated color coming from backend/consumers.
// Returns the input only when it is already achromatic (saturation ≤ 20);
// otherwise returns the achromatic `fallback`.
const V12_SAFE_GRAYSCALE = new Set([
    "#FFFFFF", "#FAFAFA", "#F5F5F5", "#E5E5E5", "#D4D4D4", "#CCCCCC",
    "#A3A3A3", "#737373", "#525252", "#404040", "#262626", "#171717",
    "#111111", "#000000",
]);
function v12SafeColor(input, fallback = "#A3A3A3") {
    if (!input || typeof input !== "string") return fallback;
    const hex = input.toUpperCase().trim();
    if (V12_SAFE_GRAYSCALE.has(hex)) return hex;
    const m = hex.match(/^#([0-9A-F]{6})$/i);
    if (!m) return fallback;
    const n = parseInt(m[1], 16);
    const r = (n >> 16) & 0xFF, g = (n >> 8) & 0xFF, b = n & 0xFF;
    const sat = Math.max(r, g, b) - Math.min(r, g, b);
    return sat <= 20 ? hex : fallback;
}

export default function AIAgentCardV3({
    testId,
    name,
    description,
    statusKey,
    statusLabel,
    color = "#FFFFFF",
    progressPct = 0,
    delay = 0,
}) {
    const s = STATUS_COLORS[statusKey] || STATUS_COLORS.inactive;
    const safe = v12SafeColor(color);
    return (
        <motion.article
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay }}
            data-testid={testId}
            className="rounded-2xl border p-5"
            style={{ backgroundColor: "#111111", borderColor: "#1A1A1A" }}
        >
            <div className="flex items-center justify-between">
                <span
                    className="h-10 w-10 rounded-xl grid place-items-center"
                    style={{ backgroundColor: `${safe}22` }}
                >
                    <Bot className="h-5 w-5" style={{ color: safe }} aria-hidden />
                </span>
                <span
                    className="text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full"
                    style={{ backgroundColor: s.bg, color: s.fg }}
                >
                    {statusLabel}
                </span>
            </div>
            <p className="mt-4 text-sm font-semibold text-[#FFFFFF]">{name}</p>
            <p className="mt-1 text-xs text-[#CCCCCC]">{description}</p>
            <div
                className="mt-4 h-1 rounded-full overflow-hidden"
                style={{ backgroundColor: "#1A1A1A" }}
                role="progressbar"
                aria-valuenow={progressPct}
                aria-valuemin={0}
                aria-valuemax={100}
            >
                <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${progressPct}%`, backgroundColor: safe }}
                />
            </div>
        </motion.article>
    );
}
